var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Divider, Grid, Step, StepLabel, Stepper, Typography, Stack, FormLabel, FilledInput, Tooltip, IconButton, useColorScheme, TextField } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { TbArrowBack, TbChevronLeft, TbChevronRight } from 'react-icons/tb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { green } from '@mui/material/colors';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import InputMask from 'react-input-mask';
import 'react-phone-number-input/style.css';
import { fetchAddressSuggestions } from './mapbox';
import { V2_API_URL } from '@settings';
import useAccessToken from '@hooks/useAccessToken';
import useCustomer from '@hooks/useCustomer';
import HelperTextInput from '@components/HelperTextInput';
// ----- NEW: for translations
import { useTranslation } from 'react-i18next';
const stepsKeys = [
    'checkout.steps.personalInformation',
    'checkout.steps.payment',
    'checkout.steps.nextSteps',
];
if (window.Intercom) {
    window.Intercom('shutdown');
}
export default function IntegratedCheckout({ state }) {
    var _a, _b, _c;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const { setMode } = useColorScheme();
    const steps = stepsKeys.map((key) => t(key));
    const [buttonText, setButtonText] = useState(t('button.next'));
    const [activeStep, setActiveStep] = useState(0);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [recoveryId, setRecoveryId] = useState('');
    const [rentalId, setRentalId] = useState('');
    const [openDetails, setOpenDetails] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        formattedPhoneNumber: '',
        billingAddress: '',
    });
    const [addressOptions, setAddressOptions] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errors, setErrors] = useState({});
    // Set light mode on mount
    useEffect(() => {
        setMode('light');
    }, [setMode]);
    // Debounced cart recovery trigger when email changes
    useEffect(() => {
        const debounceTimeout = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            if (customerInfo.email && customerInfo.email.trim()) {
                try {
                    const response = yield fetch(`${V2_API_URL}/public/cartRecovery`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            firstName: customerInfo.firstName,
                            lastName: customerInfo.lastName,
                            locationId: state.rentalDetails.locationId,
                            email: customerInfo.email,
                            bookingInfo: {
                                customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
                                boatName: state.rentalDetails.boatName,
                                customerId: state.rentalDetails.customerId,
                                locationId: state.rentalDetails.locationId,
                                boatId: state.rentalDetails.boatId,
                                startDate: state.rentalDetails.startDate,
                                endDate: state.rentalDetails.endDate,
                                totalPrice: state.rentalPricing.totalPrice,
                                dueNow: state.depositInformation.depositDueNow,
                                dueAtLocation: state.depositInformation.remainingBalance,
                                phoneNumber: customerInfo.phoneNumber,
                                billingAddress: customerInfo.billingAddress,
                                boatAddons: state.selectedBoatAddons.selectedBoatAddons,
                                globalAddons: state.globalAddons.selectedGlobalAddons,
                            },
                        }),
                    });
                    if (!response.ok) {
                        console.error('Failed to send recovery email:', response.statusText);
                    }
                    const responseJson = yield response.json();
                    setRecoveryId(responseJson.id);
                }
                catch (error) {
                    console.error('Error sending recovery email:', error);
                }
            }
        }), 1000);
        return () => clearTimeout(debounceTimeout);
    }, [
        customerInfo.email,
        customerInfo.firstName,
        customerInfo.lastName,
        state.rentalDetails,
        state.rentalPricing,
        state.depositInformatßion
    ]);
    // Capture phone number and billing address immediately
    useEffect(() => {
        const captureDetails = () => __awaiter(this, void 0, void 0, function* () {
            if (customerInfo.phoneNumber.trim() || customerInfo.billingAddress.trim()) {
                try {
                    const response = yield fetch(`${V2_API_URL}/public/cartRecovery`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            firstName: customerInfo.firstName,
                            lastName: customerInfo.lastName,
                            locationId: state.rentalDetails.locationId,
                            email: customerInfo.email,
                            bookingInfo: {
                                boatName: state.rentalDetails.boatName,
                                customerId: state.rentalDetails.customerId,
                                locationId: state.rentalDetails.locationId,
                                boatId: state.rentalDetails.boatId,
                                startDate: state.rentalDetails.startDate,
                                endDate: state.rentalDetails.endDate,
                                totalPrice: state.rentalPricing.totalPrice,
                                dueNow: state.depositInformation.depositDueNow,
                                dueAtLocation: state.depositInformation.remainingBalance,
                                phoneNumber: customerInfo.phoneNumber,
                                billingAddress: customerInfo.billingAddress,
                                boatAddons: state.selectedBoatAddons.selectedBoatAddons,
                                globalAddons: state.globalAddons.selectedGlobalAddons,
                            },
                        }),
                    });
                    if (!response.ok) {
                        console.error('Failed to send recovery email:', response.statusText);
                    }
                    const responseJson = yield response.json();
                    setRecoveryId(responseJson.id);
                }
                catch (error) {
                    console.error('Error sending recovery email:', error);
                }
            }
        });
        captureDetails();
    }, [
        customerInfo.phoneNumber,
        customerInfo.billingAddress,
        customerInfo.firstName,
        customerInfo.lastName,
        customerInfo.email,
        state.rentalDetails,
        state.rentalPricing,
        state.depositInformation
    ]);
    // Save cart recovery every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => __awaiter(this, void 0, void 0, function* () {
            if (customerInfo.email && customerInfo.email.trim()) {
                try {
                    const response = yield fetch(`${V2_API_URL}/public/cartRecovery`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            firstName: customerInfo.firstName,
                            lastName: customerInfo.lastName,
                            locationId: state.rentalDetails.locationId,
                            email: customerInfo.email,
                            bookingInfo: {
                                boatName: state.rentalDetails.boatName,
                                customerId: state.rentalDetails.customerId,
                                locationId: state.rentalDetails.locationId,
                                boatId: state.rentalDetails.boatId,
                                startDate: state.rentalDetails.startDate,
                                endDate: state.rentalDetails.endDate,
                                totalPrice: state.rentalPricing.totalPrice,
                                dueNow: state.depositInformation.depositDueNow,
                                dueAtLocation: state.depositInformation.remainingBalance,
                                phoneNumber: customerInfo.phoneNumber,
                                billingAddress: customerInfo.billingAddress,
                                boatAddons: state.selectedBoatAddons.selectedBoatAddons,
                                globalAddons: state.globalAddons.selectedGlobalAddons,
                            },
                        }),
                    });
                    if (!response.ok) {
                        console.error('Failed to send recovery email:', response.statusText);
                    }
                    const responseJson = yield response.json();
                    setRecoveryId(responseJson.id);
                }
                catch (error) {
                    console.error('Error sending recovery email:', error);
                }
            }
        }), 10000);
        return () => clearInterval(interval);
    }, [
        customerInfo.email,
        customerInfo.firstName,
        customerInfo.lastName,
        state.rentalDetails,
        state.rentalPricing,
        state.depositInformation,
        customerInfo.phoneNumber,
        customerInfo.billingAddress
    ]);
    const { rentalDetails, depositInformation, discountDetails, rentalPricing, selectedBoatAddons, globalAddons, extendedData } = state;
    // ----- VALIDATION -----
    const validateFields = () => __awaiter(this, void 0, void 0, function* () {
        const newErrors = {};
        if (!customerInfo.firstName.trim()) {
            newErrors.firstName = t('checkout.errors.firstNameRequired');
        }
        if (!customerInfo.lastName.trim()) {
            newErrors.lastName = t('checkout.errors.lastNameRequired');
        }
        if (!customerInfo.email.trim()) {
            newErrors.email = t('checkout.errors.emailRequired');
        }
        else if (!/^\S+@\S+\.\S+$/.test(customerInfo.email)) {
            newErrors.email = t('checkout.errors.invalidEmail');
        }
        if (!accessToken) {
            if (!customerInfo.phoneNumber.trim()) {
                newErrors.phoneNumber = t('checkout.errors.phoneRequired');
            }
            else {
                const phoneNumber = parsePhoneNumberFromString(customerInfo.phoneNumber, 'US');
                if (!phoneNumber || !phoneNumber.isValid()) {
                    newErrors.phoneNumber = t('checkout.errors.invalidPhone');
                }
            }
        }
        if (!accessToken && !customerInfo.billingAddress.trim()) {
            newErrors.billingAddress = t('checkout.errors.addressRequired');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    });
    // ----- HANDLERS -----
    // Modified handlePayment now also handles booking without payment when an auth token exists.
    const handlePayment = () => __awaiter(this, void 0, void 0, function* () {
        var _a;
        let paymentMethodId = '';
        if (!accessToken) {
            if (!stripe || !elements) {
                console.error('Stripe not loaded');
                return;
            }
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                console.error('No cardElement found');
                return;
            }
            const paymentMethodRes = yield stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: `${customerInfo.firstName} ${customerInfo.lastName}`,
                    email: customerInfo.email,
                    address: { line1: customerInfo.billingAddress },
                },
            });
            if (paymentMethodRes.error) {
                console.error('Error creating PaymentMethod:', paymentMethodRes.error.message);
                setPaymentError(paymentMethodRes.error.message || 'Failed to create payment method');
                return;
            }
            paymentMethodId = (_a = paymentMethodRes.paymentMethod) === null || _a === void 0 ? void 0 : _a.id;
            if (!paymentMethodId) {
                setPaymentError('No paymentMethod ID returned');
                return;
            }
        }
        else {
            // Authenticated users book without a payment method.
            paymentMethodId = '';
            state.depositInformation.remainingBalance += state.depositInformation.depositDueNow;
            state.depositInformation.depositDueNow = 0;
            state.depositInformation.depositPercentage = 0;
        }
        if (state.discountDetails.discount > 0) {
            const discountAmount = (state.rentalPricing.subtotal * state.discountDetails.discount) / 100;
            state.discountDetails.discount = discountAmount;
        }
        const bigPayload = Object.assign(Object.assign({}, state), { forceBook: accessToken ? true : false, customerInfo,
            paymentMethodId, cartRecoveryId: recoveryId, TechnicalDetails: {
                IpAddress: yield fetch('https://api.ipify.org?format=json')
                    .then((res) => res.json())
                    .then((data) => data.ip),
                BrowserInfo: navigator.userAgent,
            } });
        try {
            setIsProcessing(true);
            setButtonText('Processing...');
            const resp = yield fetch(`${V2_API_URL}/transaction/book`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
                body: JSON.stringify(bigPayload),
            });
            const bookingJson = yield resp.json();
            if (!resp.ok) {
                setPaymentError(bookingJson.error || 'Booking failed');
                setIsProcessing(false);
                setButtonText(!accessToken ? 'Pay' : t('button.bookNow'));
                return;
            }
            setRentalId(bookingJson.rental_id);
            // If the user is authenticated, skip the payment and review steps
            if (accessToken) {
                setActiveStep(steps.length); // Go directly to confirmation
            }
            else {
                setActiveStep((prev) => prev + 1);
            }
            setPaymentSuccess(true);
            setIsProcessing(false);
            setButtonText('Redirecting...');
            setTimeout(() => {
                if (!accessToken) {
                    navigate(`/next-steps?id=${bookingJson.rental_id}`);
                }
                else {
                    navigate(`/bookings/${bookingJson.rental_id}`);
                }
            }, 2000);
        }
        catch (err) {
            console.error('Error booking:', err);
            setPaymentError('Unexpected booking error');
            setIsProcessing(false);
        }
    });
    // Modified handleNext: for authenticated users (with accessToken) we immediately call handlePayment from step 0.
    const handleNext = () => __awaiter(this, void 0, void 0, function* () {
        console.log('handleNext triggered, activeStep:', activeStep);
        if (activeStep === 0) {
            const isValid = yield validateFields();
            if (!isValid)
                return;
            if (accessToken) {
                console.log('Access token present – auto-submitting booking');
                yield handlePayment();
                return;
            }
        }
        if (activeStep === 1) {
            yield handlePayment();
            return;
        }
        setActiveStep((prev) => prev + 1);
        const isLastStep = activeStep === steps.length - 2;
        setButtonText(isLastStep ? t('button.bookNow') : t('button.next'));
    });
    const toggleDrawer = (newOpen) => () => {
        setOpenDetails(newOpen);
    };
    // Internal Renting
    const { accessToken } = useAccessToken();
    const { data: customer } = useCustomer();
    const customerId = (customer === null || customer === void 0 ? void 0 : customer.id) || '';
    const [allRenters, setAllRenters] = useState([]);
    const [isLoadingRenters, setIsLoadingRenters] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    useEffect(() => {
        setCustomerInfo({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            formattedPhoneNumber: '',
            billingAddress: '',
        });
        setSearchInput('');
        setSelectedCustomer(null);
        fetchAllRenters();
    }, []);
    const fetchAllRenters = () => __awaiter(this, void 0, void 0, function* () {
        if (!customerId)
            return;
        try {
            setIsLoadingRenters(true);
            const resp = yield fetch(`${V2_API_URL}/renters?customerId=${customerId}`);
            if (!resp.ok) {
                throw new Error('Failed to fetch renters');
            }
            const data = yield resp.json();
            const renters = data.customers || data;
            setAllRenters(renters);
        }
        catch (err) {
            console.error('Error fetching renters:', err);
        }
        finally {
            setIsLoadingRenters(false);
        }
    });
    useEffect(() => {
        if (selectedCustomer) {
            setCustomerInfo({
                firstName: selectedCustomer.firstName,
                lastName: selectedCustomer.lastName,
                email: selectedCustomer.email,
                phoneNumber: selectedCustomer.phoneNumber,
                formattedPhoneNumber: selectedCustomer.phoneNumber,
                billingAddress: selectedCustomer.billingAddress || '',
            });
        }
    }, [selectedCustomer]);
    const filterOptions = (options, state) => {
        const query = state.inputValue.toLowerCase().trim();
        if (!query)
            return options;
        return options.filter((cust) => {
            const first = cust.firstName.toLowerCase();
            const last = cust.lastName.toLowerCase();
            const em = cust.email.toLowerCase();
            const phoneDigits = cust.phoneNumber.replace(/\D/g, '');
            const queryDigits = query.replace(/\D/g, '');
            return (first.includes(query) ||
                last.includes(query) ||
                em.includes(query) ||
                (queryDigits.length > 0 && phoneDigits.includes(queryDigits)));
        });
    };
    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (_jsxs(Box, { maxWidth: "500px", children: [!accessToken && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('checkout.stepsContent.personalInformation') }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { item: true, xs: 12, sm: 6, pr: 2, children: [_jsx(FormLabel, { htmlFor: "first-name", required: true, children: t('checkout.form.firstName') }), _jsx(FilledInput, { id: "first-name", name: "first-name", type: "text", fullWidth: true, required: true, value: customerInfo.firstName, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { firstName: e.target.value }))), error: !!errors.firstName, placeholder: t('checkout.form.firstNamePlaceholder') || '' }), errors.firstName && (_jsx(Typography, { color: "error", variant: "caption", children: errors.firstName }))] }), _jsxs(Grid, { item: true, xs: 12, sm: 6, pr: 2, children: [_jsx(FormLabel, { htmlFor: "last-name", required: true, children: t('checkout.form.lastName') }), _jsx(FilledInput, { id: "last-name", name: "last-name", type: "text", fullWidth: true, required: true, value: customerInfo.lastName, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { lastName: e.target.value }))), error: !!errors.lastName, placeholder: t('checkout.form.lastNamePlaceholder') || '' }), errors.lastName && (_jsx(Typography, { color: "error", variant: "caption", children: errors.lastName }))] }), _jsxs(Grid, { item: true, xs: 12, sm: 6, pr: 2, children: [_jsx(FormLabel, { htmlFor: "email", required: true, children: t('checkout.form.email') }), _jsx(FilledInput, { id: "email", name: "email", type: "email", fullWidth: true, autoComplete: "email", required: true, value: customerInfo.email, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { email: e.target.value }))), error: !!errors.email, placeholder: t('checkout.form.emailPlaceholder') || 'john.doe@example.com' }), errors.email && (_jsx(Typography, { color: "error", variant: "caption", children: errors.email }))] }), _jsxs(Grid, { item: true, xs: 12, sm: 6, pr: 2, children: [_jsx(FormLabel, { htmlFor: "phone", required: true, children: t('checkout.form.phoneNumber') }), _jsx(InputMask, { mask: "999-999-9999", value: customerInfo.phoneNumber, onChange: (e) => {
                                                        const input = e.target.value;
                                                        setCustomerInfo((prev) => {
                                                            var _a;
                                                            return (Object.assign(Object.assign({}, prev), { phoneNumber: input, formattedPhoneNumber: ((_a = parsePhoneNumberFromString(input, 'US')) === null || _a === void 0 ? void 0 : _a.format('E.164')) || '' }));
                                                        });
                                                    }, onBlur: () => {
                                                        const pn = parsePhoneNumberFromString(customerInfo.phoneNumber, 'US');
                                                        if (pn && pn.isValid()) {
                                                            setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { formattedPhoneNumber: pn.format('E.164') })));
                                                        }
                                                    }, children: () => (_jsx(TextField, { variant: "filled", fullWidth: true, placeholder: "613-123-4567", error: !!errors.phoneNumber, helperText: errors.phoneNumber, InputProps: { disableUnderline: true } })) })] }), _jsxs(Grid, { item: true, xs: 12, pr: 2, children: [_jsx(FormLabel, { htmlFor: "address", required: true, children: t('checkout.form.billingAddress') }), _jsx(Autocomplete, { freeSolo: true, options: addressOptions.map((option) => option.place_name), onInputChange: (_event, value) => __awaiter(this, void 0, void 0, function* () {
                                                        setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { billingAddress: value })));
                                                        if (value.length > 3) {
                                                            const suggestions = yield fetchAddressSuggestions(value);
                                                            setAddressOptions(suggestions);
                                                        }
                                                        else {
                                                            setAddressOptions([]);
                                                        }
                                                    }), onChange: (_event, value) => {
                                                        if (typeof value === 'string') {
                                                            setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { billingAddress: value })));
                                                        }
                                                    }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { variant: "filled", placeholder: t('checkout.form.addressPlaceholder') || '', error: !!errors.billingAddress, helperText: errors.billingAddress }))), sx: { width: '100%' } })] })] })] })), accessToken && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: { mb: 2 } }), _jsx(Typography, { variant: "body2", sx: { mb: 1 }, children: "Select Existing Customer (Optional)" }), _jsx(Autocomplete, { options: allRenters, loading: isLoadingRenters, loadingText: "Loading...", getOptionLabel: (option) => `${option.firstName} ${option.lastName} (${option.email})`, filterOptions: filterOptions, inputValue: searchInput, onInputChange: (_event, newVal) => setSearchInput(newVal), value: selectedCustomer, onChange: (_event, newValue) => {
                                        setSelectedCustomer(newValue);
                                    }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { placeholder: "Type name, email, or phone" }))), sx: { mb: 2 } }), _jsx(Divider, { sx: { mb: 2 } }), !selectedCustomer && (_jsxs(Stack, { spacing: 2, sx: { mb: 2 }, children: [_jsx(HelperTextInput, { label: "First Name", value: customerInfo.firstName, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { firstName: e.target.value }))) }), _jsx(HelperTextInput, { label: "Last Name", value: customerInfo.lastName, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { lastName: e.target.value }))) }), _jsx(HelperTextInput, { label: "Email", value: customerInfo.email, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { email: e.target.value }))) }), _jsx(HelperTextInput, { label: "Phone", value: customerInfo.phoneNumber, onChange: (e) => setCustomerInfo((prev) => (Object.assign(Object.assign({}, prev), { phoneNumber: e.target.value }))) })] })), selectedCustomer && (_jsxs(Box, { sx: { mb: 2 }, children: [_jsxs(Typography, { variant: "body2", children: [_jsx("strong", { children: "Customer:" }), " ", selectedCustomer.firstName, " ", selectedCustomer.lastName] }), _jsxs(Typography, { variant: "body2", children: [_jsx("strong", { children: "Email:" }), " ", selectedCustomer.email] }), _jsxs(Typography, { variant: "body2", children: [_jsx("strong", { children: "Phone:" }), " ", selectedCustomer.phoneNumber] }), _jsxs(Typography, { variant: "body2", children: [_jsx("strong", { children: "Address:" }), " ", selectedCustomer.billingAddress] })] }))] }))] }));
            case 1:
                return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('checkout.stepsContent.paymentDetails') }), _jsx(Divider, { sx: { mb: 2 } }), _jsx(Box, { sx: {
                                padding: 2,
                                borderRadius: 2,
                                boxShadow: '0 1px 5px rgba(0, 0, 0, 0.1)',
                                bgcolor: 'background.paper',
                                mb: 2,
                            }, children: _jsx(CardElement, { options: {
                                    style: {
                                        base: {
                                            fontSize: '16px',
                                            color: '#424770',
                                            '::placeholder': { color: '#aab7c4' },
                                        },
                                        invalid: { color: '#9e2146' },
                                    },
                                } }) }), paymentError && (_jsx(Typography, { color: "error", variant: "body2", gutterBottom: true, children: paymentError }))] }));
            case 2:
                return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('checkout.stepsContent.reviewYourOrder') }), _jsxs(Box, { mb: 2, children: [_jsx(Typography, { variant: "subtitle1", gutterBottom: true, children: t('checkout.personalInformation') }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsxs(Typography, { variant: "body2", children: [_jsxs("strong", { children: [t('checkout.form.firstName'), ":"] }), " ", customerInfo.firstName] }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsxs(Typography, { variant: "body2", children: [_jsxs("strong", { children: [t('checkout.form.lastName'), ":"] }), " ", customerInfo.lastName] }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsxs(Typography, { variant: "body2", children: [_jsxs("strong", { children: [t('checkout.form.email'), ":"] }), " ", customerInfo.email] }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsxs(Typography, { variant: "body2", children: [_jsxs("strong", { children: [t('checkout.form.phoneNumber'), ":"] }), " ", customerInfo.phoneNumber] }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(Typography, { variant: "body2", children: [_jsxs("strong", { children: [t('checkout.form.billingAddress'), ":"] }), " ", customerInfo.billingAddress] }) })] })] })] }));
            default:
                return _jsx(Typography, { children: t('checkout.unavailableStep') });
        }
    };
    return (_jsxs(Grid, { container: true, sx: { height: { xs: '100%', sm: '90%' } }, children: [_jsxs(Grid, { item: true, xs: 12, sm: 6, lg: 6, sx: {
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    backgroundColor: 'background.paper',
                    borderRight: { sm: 'none', md: '1px solid' },
                    borderColor: { sm: 'none', md: 'divider' },
                    alignItems: 'start',
                    pt: 4,
                    px: 10,
                    gap: 4,
                }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'end', height: 150 }, children: _jsx(Button, { startIcon: _jsx(TbArrowBack, {}), onClick: state.handleGoBack, sx: { ml: '-8px', gap: 1 }, children: t('button.back') }) }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%', maxWidth: 500 }, children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('checkout.orderSummaryTitle') }), _jsxs(Box, { children: [_jsxs(Typography, { children: [t('checkout.bookingForLabel'), " \"", extendedData.boatRefresh.BoatName, "\""] }), _jsxs(Typography, { children: [t('checkout.fromLabel'), " ", _jsx("strong", { children: rentalDetails.startDate }), " ", t('checkout.toLabel'), ' ', _jsx("strong", { children: rentalDetails.endDate })] })] }), _jsx(Divider, { sx: { mb: 2 } }), _jsxs(Box, { mb: 2, children: [_jsx(Typography, { variant: "subtitle1", gutterBottom: true, children: t('checkout.orderSummary') }), _jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "body2", children: t('checkout.subtotal') }), _jsxs(Typography, { variant: "body2", children: ["$", rentalPricing.subtotal.toFixed(2)] })] }), discountDetails.discount > 0 && (_jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "body2", children: t('checkout.discount', { discount: discountDetails.discount }) }), _jsxs(Typography, { variant: "body2", children: ["- $", ((rentalPricing.subtotal * discountDetails.discount) / 100).toFixed(2)] })] })), selectedBoatAddons.selectedBoatAddonsTotal > 0 && (_jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "body2", children: t('bookingBreakdown.addOns') }), _jsxs(Typography, { variant: "body2", children: ["+ $", selectedBoatAddons.selectedBoatAddonsTotal.toFixed(2)] })] })), discountDetails.giftCardAmount > 0 && (_jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "body2", children: t('checkout.giftCardPromo') }), _jsxs(Typography, { variant: "body2", children: ["- $", ((rentalPricing.subtotal * discountDetails.giftCardAmount) / 100).toFixed(2)] })] })), _jsxs(Box, { display: "flex", alignItems: "center", mt: 1, children: [_jsxs(Typography, { variant: "body1", children: [t('checkout.taxesCoverageFees'), "*"] }), _jsx(Tooltip, { title: _jsxs(_Fragment, { children: [(_a = rentalPricing.tax) === null || _a === void 0 ? void 0 : _a.map((item) => (item.boat && (_jsxs("div", { children: [item.name, ": ", item.rate, "%"] }, item.id)))), rentalDetails.feePercentage && (_jsxs("div", { children: [t('checkout.platformFee'), ": ", (rentalDetails.platformFee * 100).toFixed(2), "%"] })), rentalDetails.feePercentage && (_jsxs("div", { children: [t('checkout.transactionFee'), ": ", (rentalDetails.interchangeFee * 100).toFixed(2), "%"] })), (_b = globalAddons.selectedGlobalAddons) === null || _b === void 0 ? void 0 : _b.map((addon) => (_jsxs("div", { children: [addon.name, ": ", addon.percentage * 100, "% (min $", addon.minimum, ")"] }, addon.name)))] }), children: _jsx(IconButton, { size: "small", sx: { ml: 1 }, children: _jsx(InfoIcon, { fontSize: "small" }) }) }), _jsxs(Typography, { variant: "body1", sx: { ml: 'auto' }, children: ["$", (_c = rentalPricing.fees) === null || _c === void 0 ? void 0 : _c.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })] })] }), _jsx(Divider, { sx: { my: 2 } }), _jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "h6", children: t('checkout.grandTotal') }), _jsxs(Typography, { variant: "h6", children: ["$", rentalPricing.totalPrice.toFixed(2)] })] }), depositInformation.depositDueNow > 0 && (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "body2", children: t('checkout.depositDueNow', { depositPercentage: depositInformation.depositPercentage }) }), _jsxs(Typography, { variant: "body2", children: ["$", depositInformation.depositDueNow.toFixed(2)] })] }), _jsxs(Box, { display: "flex", justifyContent: "space-between", my: 1, children: [_jsx(Typography, { variant: "body2", children: t('checkout.remainingBalance') }), _jsxs(Typography, { variant: "body2", children: ["$", depositInformation.remainingBalance.toFixed(2)] })] })] }))] })] })] }), _jsxs(Grid, { item: true, xs: 12, sm: 6, lg: 6, sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    backgroundColor: { xs: 'transparent', sm: 'background.default' },
                    alignItems: 'start',
                    pt: { xs: 2, sm: 4 },
                    px: { xs: 2, sm: 10 },
                    gap: { xs: 4, md: 8 },
                }, children: [_jsx(Box, { sx: {
                            display: { xs: 'flex', md: 'none' },
                            width: '100%',
                            justifyContent: 'space-between',
                            mb: 2,
                        }, children: _jsx(Button, { startIcon: _jsx(TbArrowBack, {}), onClick: state.handleGoBack, children: t('button.back') }) }), _jsxs(Box, { sx: {
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexGrow: 1,
                            height: 150,
                            width: '100%',
                            maxWidth: 600,
                        }, children: [_jsx("div", {}), _jsx(Stepper, { activeStep: activeStep, sx: { width: '100%', height: 40 }, children: steps.map((label) => (_jsx(Step, { sx: { ':first-of-type': { pl: 0 }, ':last-of-type': { pr: 0 } }, children: _jsx(StepLabel, { children: label }) }, label))) })] }), _jsx(Card, { sx: {
                            display: { xs: 'flex', md: 'none' },
                            width: '100%',
                            position: 'relative',
                            zIndex: 999,
                        }, children: _jsxs(CardContent, { sx: {
                                display: 'flex',
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                ':last-of-type': { pb: 2 },
                            }, children: [_jsx("div", { children: state.depositInformation.depositDueNow > 0 ? (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle2", gutterBottom: true, children: t('checkout.dueNowLabel') }), _jsxs(Typography, { children: ["$", state.depositInformation.depositDueNow.toFixed(2)] })] })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle2", gutterBottom: true, children: "Total" }), _jsxs(Typography, { children: ["$", state.rentalPricing.totalPrice.toFixed(2)] })] })) }), _jsx(Box, { sx: { height: openDetails ? '300px' : '100vph' }, children: _jsx(Button, { variant: "text", color: "primary", endIcon: _jsx(ExpandMoreRoundedIcon, {}), onClick: toggleDrawer(true), children: t('checkout.viewDetails') }) }), openDetails && (_jsxs(Box, { sx: {
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        bgcolor: 'background.paper',
                                        borderTopLeftRadius: 16,
                                        borderTopRightRadius: 16,
                                        padding: 3,
                                        maxHeight: '90vh',
                                        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                                    }, children: [_jsxs(Grid, { container: true, alignItems: "center", spacing: 1, children: [_jsx(Grid, { item: true, xs: 2 }), _jsx(Grid, { item: true, xs: 8, children: _jsx(Typography, { variant: "h6", align: "center", children: t('checkout.orderSummary') }) }), _jsx(Grid, { item: true, xs: 2, container: true, justifyContent: "flex-end", children: _jsx(IconButton, { onClick: toggleDrawer(false), "aria-label": "close drawer", children: _jsx(CloseIcon, {}) }) })] }), _jsx(Divider, { sx: { my: 2 } }), _jsxs(Stack, { spacing: 2, children: [[
                                                    { label: t('checkout.subtotal'), value: state.rentalPricing.subtotal },
                                                    { label: t('checkout.discount'), value: -state.discountDetails.discount },
                                                    { label: t('checkout.giftCardPromo'), value: -state.discountDetails.giftCardAmount },
                                                    { label: t('checkout.taxesCoverageFees'), value: state.rentalPricing.fees },
                                                ].map((item, index) => (_jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 8, children: _jsx(Typography, { variant: "body1", color: "text.secondary", children: item.label }) }), _jsx(Grid, { item: true, xs: 4, textAlign: "right", children: _jsx(Typography, { variant: "body1", fontWeight: 500, children: item.value < 0
                                                                    ? `- $${Math.abs(item.value).toFixed(2)}`
                                                                    : `$${item.value.toFixed(2)}` }) })] }, index))), state.depositInformation.depositDueNow > 0 ? (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 8, children: _jsx(Typography, { variant: "body1", fontWeight: 600, children: t('checkout.depositDueNow', { depositPercentage: state.depositInformation.depositPercentage }) }) }), _jsx(Grid, { item: true, xs: 4, textAlign: "right", children: _jsxs(Typography, { variant: "body1", fontWeight: 600, children: ["$", state.depositInformation.depositDueNow.toFixed(2)] }) })] }), _jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 8, children: _jsx(Typography, { variant: "body1", fontWeight: 600, color: "text.secondary", children: t('checkout.remainingBalance') }) }), _jsx(Grid, { item: true, xs: 4, textAlign: "right", children: _jsxs(Typography, { variant: "body1", fontWeight: 600, color: "text.secondary", children: ["$", state.depositInformation.remainingBalance.toFixed(2)] }) })] }), _jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 8, children: _jsx(Typography, { variant: "body1", color: "text.secondary", children: t('checkout.total') }) }), _jsx(Grid, { item: true, xs: 4, textAlign: "right", children: _jsxs(Typography, { variant: "body1", color: "text.secondary", children: ["$", state.rentalPricing.totalPrice.toFixed(2)] }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 8, children: _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Grand Total" }) }), _jsx(Grid, { item: true, xs: 4, textAlign: "right", children: _jsxs(Typography, { variant: "subtitle1", fontWeight: 600, children: ["$", state.rentalPricing.totalPrice.toFixed(2)] }) })] })] }))] })] }))] }) }), _jsx(Stepper, { activeStep: activeStep, alternativeLabel: true, sx: {
                            display: { xs: 'flex', md: 'none' },
                            flexDirection: 'row',
                            width: '100%',
                            height: 40,
                            marginBottom: 4,
                            justifyContent: 'space-between',
                        }, children: steps.map((label) => (_jsx(Step, { children: _jsx(StepLabel, { children: label }) }, label))) }), _jsx(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            width: '100%',
                            maxWidth: { xs: '100%', md: 600 },
                            gap: { xs: 5, md: 0 },
                        }, children: activeStep === steps.length ? (_jsxs(Stack, { spacing: 2, useFlexGap: true, children: [_jsx(CheckCircleIcon, { sx: { fontSize: 60, color: green[500], alignSelf: 'center' } }), _jsx(Typography, { variant: "h5", align: "center", children: t('checkout.thankYouForOrder') }), _jsx(Typography, { align: "center", sx: { color: 'text.secondary' }, children: t('checkout.orderNumber', { id: rentalId || '######' }) + ' ' + t('checkout.confirmationEmailed') }), _jsx(Button, { variant: "contained", sx: { alignSelf: 'center', width: { xs: '100%', sm: 'auto' } }, onClick: () => navigate('/bookings'), children: t('checkout.goToMyOrders') })] })) : (_jsxs(_Fragment, { children: [getStepContent(activeStep), _jsxs(Box, { sx: [
                                        {
                                            display: 'flex',
                                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                                            alignItems: 'end',
                                            flexGrow: 1,
                                            gap: 1,
                                            pb: { xs: 12, sm: 0 },
                                            mt: { xs: 2, sm: 0 },
                                            mb: '60px',
                                        },
                                        activeStep !== 0
                                            ? { justifyContent: 'space-between' }
                                            : { justifyContent: 'flex-end' },
                                    ], children: [activeStep !== 0 && (_jsxs(_Fragment, { children: [_jsx(Button, { startIcon: _jsx(TbChevronLeft, {}), onClick: () => setActiveStep((prev) => prev - 1), variant: "text", sx: { display: { xs: 'none', sm: 'flex' } }, children: t('button.previous') }), _jsx(Button, { startIcon: _jsx(TbChevronLeft, {}), onClick: () => setActiveStep((prev) => prev - 1), variant: "outlined", fullWidth: true, sx: { display: { xs: 'flex', sm: 'none' } }, children: t('button.previous') })] })), _jsxs(Button, { variant: "contained", endIcon: _jsx(TbChevronRight, {}), onClick: handleNext, sx: { width: { xs: '100%', sm: 'fit-content' } }, disabled: isProcessing || paymentSuccess, children: [buttonText, isProcessing && _jsx(CircularProgress, { size: 24, sx: { color: 'white', ml: 2 } })] })] })] })) })] })] }));
}
